import { importLibrary, setOptions, type LibraryMap } from '@googlemaps/js-api-loader';
import { classes, selectors } from './init';

type Coordinates = { lat: number; lng: number };

type LocationData = {
	id: string;
	title: string;
	lat: string;
	lng: string;
	website?: string;
	email?: string;
	phone?: string;
	marker_icon?: string;
	custom_marker_icon?: string | false;
};

type MapData = { hasFocus: string | null; center: Coordinates; locations: LocationData[] };

type MapInstance = InstanceType<LibraryMap['maps']['Map']>;
type InfoWindowInstance = InstanceType<LibraryMap['maps']['InfoWindow']>;
type MarkerInstance = InstanceType<LibraryMap['marker']['AdvancedMarkerElement']>;
type MarkerOptions = ConstructorParameters<LibraryMap['marker']['AdvancedMarkerElement']>[0];
type GoogleMapsClasses = Pick<LibraryMap['maps'], 'Map' | 'InfoWindow'> & Pick<LibraryMap['marker'], 'AdvancedMarkerElement'>;

// Configuration constants
const MAP_CONFIG = {
	API_KEY: 'AIzaSyD4B6o-I6VE_lO9AMq4L6OFiHDckOKimfo',
	DEFAULT_ZOOM: 14,
	FOCUSED_ZOOM: 15,
	CONTROLS: {
		zoomControl: true,
		streetViewControl: false,
		mapTypeControl: false,
	},
};

const LABELS = {
	EMAIL: 'Mail',
};

setOptions({ key: MAP_CONFIG.API_KEY });

// Global map state
let map: MapInstance | undefined;
let zoomLevel: number | undefined;
let infoWindow: InfoWindowInstance | undefined;

/**
 * Initialize all maps found in the document
 * @returns {Promise<void>}
 */
export const initMap = async () => {
	const { Map, InfoWindow } = await importLibrary('maps');
	const { AdvancedMarkerElement } = await importLibrary('marker');

	const locationContainers = document.querySelectorAll<HTMLElement>(selectors.locationContainer);

	locationContainers.forEach((locationContainer) => {
		initSingleMap(locationContainer, { Map, InfoWindow, AdvancedMarkerElement });
	});
};

/**
 * Initialize a single map instance
 * @param {HTMLElement} locationContainer - The container element for the location
 * @param {Object} googleMapsClasses - Google Maps API classes
 */
const initSingleMap = (locationContainer: HTMLElement, { Map, InfoWindow, AdvancedMarkerElement }: GoogleMapsClasses) => {
	const mapContainer = locationContainer.querySelector<HTMLElement>(selectors.map);

	if (!mapContainer) {
		console.warn('Map container not found in location container');
		return;
	}

	const mapData = extractMapData(mapContainer);
	zoomLevel = mapData.hasFocus ? MAP_CONFIG.FOCUSED_ZOOM : MAP_CONFIG.DEFAULT_ZOOM;

	// Initialize map and info window
	map = createMapInstance(mapContainer, mapData, Map);
	infoWindow = new InfoWindow();

	// Set up locations and interactions
	setupLocationInteractions(mapData.locations);
	createMarkers(mapData.locations, AdvancedMarkerElement);
};

/**
 * Extract map data from container attributes
 * @param {HTMLElement} mapContainer - The map container element
 * @returns {Object} Map configuration data
 */
const extractMapData = (mapContainer: HTMLElement): MapData => {
	const hasFocus = mapContainer.getAttribute('data-focus');
	const centerLat = Number(mapContainer.getAttribute('data-center-lat'));
	const centerLng = Number(mapContainer.getAttribute('data-center-lng'));
	const locationsData = mapContainer.getAttribute('data-locations');

	let locations: LocationData[] = [];
	try {
		locations = JSON.parse(locationsData || '[]') as LocationData[];
	} catch (error) {
		console.error('Failed to parse locations data:', error);
	}

	return {
		hasFocus,
		center: { lat: centerLat, lng: centerLng },
		locations,
	};
};

/**
 * Create a new Google Maps instance
 * @param {HTMLElement} mapContainer - The map container element
 * @param {Object} mapData - Map configuration data
 * @param {Object} Map - Google Maps Map class
 * @returns {Object} Google Maps instance
 */
const createMapInstance = (mapContainer: HTMLElement, mapData: MapData, Map: GoogleMapsClasses['Map']): MapInstance => {
	return new Map(mapContainer, {
		center: mapData.center,
		zoom: zoomLevel,
		...MAP_CONFIG.CONTROLS,
		mapId: getMapThemeId(),
	});
};

/**
 * Set up location list interactions if sidebar exists
 * @param {Array} locations - Array of location data
 */
const setupLocationInteractions = (locations: LocationData[]) => {
	const sidebar = document.querySelector(selectors.sidebar);
	const locationListMobile = document.querySelector(selectors.locationListMobile);

	if (!sidebar || locations.length === 0) {
		return;
	}

	// Add click listeners for desktop location list
	if (!locationListMobile) {
		addLocationClickListeners();
	}

	// Set first location as active and show it on map
	const firstLocation = locations[0];
	setActiveClass(firstLocation.id);
	showLocationInMap(firstLocation.id, zoomLevel);
};

/**
 * Add click event listeners to location list items
 */
const addLocationClickListeners = () => {
	const locationItems = document.querySelectorAll(`${selectors.locationList} .location__item`);

	locationItems.forEach((locationItem) => {
		locationItem.addEventListener('click', () => {
			const locationId = locationItem.getAttribute('id');
			showLocationInMap(locationId, zoomLevel);
		});
	});
};

/**
 * Create markers for all locations on the map
 * @param {Array} locations - Array of location data
 * @param {Object} AdvancedMarkerElement - Google Maps AdvancedMarkerElement class
 */
const createMarkers = (locations: LocationData[], AdvancedMarkerElement: GoogleMapsClasses['AdvancedMarkerElement']) => {
	locations.forEach((location) => {
		const marker = createSingleMarker(location, AdvancedMarkerElement);
		setupMarkerInfoWindow(marker, location);
	});
};

/**
 * Create a single marker for a location
 * @param {Object} location - Location data
 * @param {Object} AdvancedMarkerElement - Google Maps AdvancedMarkerElement class
 * @returns {Object} Google Maps marker instance
 */
const createSingleMarker = (location: LocationData, AdvancedMarkerElement: GoogleMapsClasses['AdvancedMarkerElement']): MarkerInstance => {
	const markerProps: MarkerOptions = {
		map,
		position: {
			lat: parseFloat(location.lat),
			lng: parseFloat(location.lng),
		},
		title: location.title,
	};

	// Set custom marker icon if available
	const iconUrl = location.marker_icon || location.custom_marker_icon;
	if (iconUrl) {
		markerProps.content = createMarkerIcon(iconUrl);
	}

	return new AdvancedMarkerElement(markerProps);
};

/**
 * Set up info window for a marker if location has contact information
 * @param {Object} marker - Google Maps marker instance
 * @param {Object} location - Location data
 */
const setupMarkerInfoWindow = (marker: MarkerInstance, location: LocationData) => {
	const hasContactInfo = location.phone || location.website || location.email;

	if (hasContactInfo) {
		marker.addListener('click', () => {
			const currentInfoWindow = infoWindow as InfoWindowInstance;
			currentInfoWindow.close();
			currentInfoWindow.setContent(generateInfoWindowContent(location));
			currentInfoWindow.open(marker.map, marker);
		});
	}
};

/**
 * Generate HTML content for info window
 * @param {Object} location - Location data
 * @returns {string} HTML content for info window
 */
const generateInfoWindowContent = (location: LocationData) => {
	const mapElement = document.querySelector(selectors.map);
	const phoneLabel = mapElement?.getAttribute('data-phone-label') || 'Phone';
	const websiteLabel = mapElement?.getAttribute('data-website-label') || 'Website';

	const contactInfo = [
		location.website && `<p>${websiteLabel}: <a href="${location.website}" target="_blank">${location.website}</a></p>`,
		location.email && `<p>${LABELS.EMAIL}: <a href="mailto:${location.email}">${location.email}</a></p>`,
		location.phone && `<p>${phoneLabel}: <a href="tel:${location.phone}">${location.phone}</a></p>`,
	]
		.filter(Boolean)
		.join('');

	return `
		<div class="location__info-window">
			<h6>${location.title}</h6>
			${contactInfo}
		</div>
	`;
};

/**
 * Create a custom marker icon element
 * @param {string} url - URL of the marker icon
 * @returns {HTMLElement} Marker icon element
 */
const createMarkerIcon = (url: string): HTMLElement => {
	const markerIcon = document.createElement('span');
	markerIcon.classList.add('marker-icon');
	markerIcon.style.setProperty('--mask-image', `url(${url})`);

	return markerIcon;
};

/**
 * Get the appropriate map theme ID based on current color scheme
 * @returns {string|null} Map theme ID or null if not available
 */
const getMapThemeId = () => {
	const theme = document.documentElement.getAttribute('data-color-scheme');
	const mapElement = document.querySelector(selectors.map);

	if (!mapElement) {
		return null;
	}

	const darkThemeId = mapElement.getAttribute('data-map-id-dark');
	const lightThemeId = mapElement.getAttribute('data-map-id-light');
	const fallbackId = lightThemeId || darkThemeId;

	return theme === 'dark' ? darkThemeId || fallbackId : lightThemeId || fallbackId;
};

/**
 * Set active class for location list item
 * @param {string} locationID - ID of the location to set as active
 */
const setActiveClass = (locationID: string | null) => {
	const locationItems = document.querySelectorAll(`${selectors.locationList} .location__item`);

	locationItems.forEach((locationItem) => {
		const tile = locationItem.querySelector('.tile');
		const isActiveLocation = locationItem.getAttribute('id') === locationID;

		// Remove active class from all tiles
		if (tile) {
			tile.classList.remove(classes.active);
		}

		// Add active class to matching location
		if (isActiveLocation && locationItem.firstChild) {
			(locationItem.firstChild as Element).classList.add(classes.active);
		}
	});
};

/**
 * Show a specific location on the map and set it as active
 * @param {string} locationID - ID of the location to show
 * @param {number} zoom - Zoom level to apply
 */
export const showLocationInMap = (locationID: string | null, zoom?: number) => {
	// Update active state in location list
	setActiveClass(locationID);

	// Exit early if map is not initialized
	if (!map) {
		console.warn('Map not initialized');
		return;
	}

	// Get location coordinates
	const coordinates = getLocationCoordinates(locationID);
	if (!coordinates) {
		console.warn(`Location with ID "${locationID}" not found`);
		return;
	}

	// Update map view
	(infoWindow as InfoWindowInstance).close();
	map.setCenter({
		lat: parseFloat(coordinates.lat),
		lng: parseFloat(coordinates.lng),
	});
	if (zoom !== undefined) {
		map.setZoom(zoom);
	}
};

/**
 * Get coordinates for a specific location ID
 * @param {string} locationID - ID of the location
 * @returns {Object|null} Location coordinates or null if not found
 */
const getLocationCoordinates = (locationID: string | null): LocationData | null => {
	const mapElement = document.querySelector(selectors.map);
	if (!mapElement) {
		return null;
	}

	try {
		const locationsData = mapElement.getAttribute('data-locations');
		const locations = JSON.parse(locationsData || '[]') as LocationData[];
		return locations.find((location) => location.id === locationID) || null;
	} catch (error) {
		console.error('Failed to parse location data:', error);
		return null;
	}
};
